import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    VIEW, EDIT, OPEN,
    INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW, ACHIEVED,
    HARD, HARD_SOFT,
} from "const"
import { OkrObjective, OkrKeyResult } from "server/models"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,

    IsOwnerOkrObjective,

    setOkrKeyResultPopupMenuTarget,
    setOkrKeyResultPopupMenuShow,
    setOkrKeyResultActive,
    addModal,
} from "client/store"
import { hasAccess } from "client/utils"
import {
    Link,
    UserIcon,
    DateFormatterString,
    TableColumn,
    TablePopover,
    TablePopoverData,
    TableMenu,
} from "client/components"
import { Confidence, ProgressPercents } from "client/pages/okr/styled"
import {
    TitleWrapper,
    TargetDescription,
    DeadlineDescription,
} from "../styled"

const Columns = (baseRoute: string, objective: OkrObjective) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)
    const updateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const editAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, EDIT)

    const isOwnerObjective = IsOwnerOkrObjective(objective, userData.userId)
    const isOwnerSession = session.owner === userData.userId
    const isOwner = isOwnerObjective || isOwnerSession

    const isOpenSession = session.status === OPEN

    const showContextMenu = (
        e: React.MouseEvent,
        active: OkrKeyResult,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setOkrKeyResultPopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setOkrKeyResultPopupMenuShow(true))
        dispatch(setOkrKeyResultActive(active))
    }

    const columns: TableColumn[] = [
        {
            field: "title",
            header: lang.title,
            cellMinWidth: "200px",
            cellMaxWidth: "300px",
            formatter: (keyResult: OkrKeyResult) => {
                const href = `${baseRoute}/${VIEW}/${keyResult.id}`

                let targetDescription: string
                if (keyResult.type === ACHIEVED) {
                    if (keyResult.actualNumber === keyResult.targetNumber) targetDescription = lang.outcomeAchieved
                    if (keyResult.actualNumber !== keyResult.targetNumber) targetDescription = lang.outcomeNotAchieved
                } else {
                    const metric = metricList[keyResult.metricId] || null
                    const metricBefore = metric && !metric.displayAfter ? metric.symbol : ""
                    const metricAfter = metric && metric.displayAfter ? metric.symbol : ""

                    const target = `${metricBefore} ${keyResult.targetNumber} ${metricAfter}`
                    const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null ? keyResult.criticalNumber : null
                    const criticalValue = critical !== null
                        ? `${metricBefore} ${keyResult.criticalNumber} ${metricAfter}`
                        : ""
                    const actual = `${metricBefore} ${keyResult.actualNumber} ${metricAfter}`

                    let descriptionStart: string
                    let descriptionEnd = ""
                    if (keyResult.type === INCREASE_TO) descriptionStart = lang.shouldIncreaseTo
                    if (keyResult.type === DECREASE_TO) descriptionStart = lang.shouldDecreaseTo
                    if (keyResult.type === STAY_ABOVE) {
                        descriptionStart = lang.shouldStayAbove
                        descriptionEnd = lang.mustBeAbove
                    }
                    if (keyResult.type === STAY_BELOW) {
                        descriptionStart = lang.shouldStayBelow
                        descriptionEnd = lang.mustBeBelow
                    }
                    targetDescription = `
                        ${descriptionStart} ${target}
                        ${critical !== null ? `${descriptionEnd} ${criticalValue}` : ""}
                        ${lang.currentProgressAt} ${actual}
                    `
                }

                let deadline: string
                if (keyResult.type !== STAY_ABOVE && keyResult.type !== STAY_BELOW) {
                    if (keyResult.deadlineType === HARD || keyResult.deadlineType === HARD_SOFT) {
                        const hardDeadlineDate = DateFormatterString(keyResult.deadlineHard, lang).date
                        deadline = lang.keyResultHardDeadlineDate.replace("{0}", hardDeadlineDate)

                        const now = new Date().getTime()
                        const hardDeadline = new Date(keyResult.deadlineHard).getTime()
                        const daysLeft = Math.round((hardDeadline - now) / (1000 * 3600 * 24))
                        let daysLeftText: string
                        if (daysLeft > 0) daysLeftText = lang.keyResultHardDeadlineDaysLeft.replace("{0}", daysLeft.toString())
                        else daysLeftText = lang.keyResultHardDeadlineDaysAgo.replace("{0}", Math.abs(daysLeft).toString())
                        deadline = `${deadline}, ${daysLeftText}`
                    }
                    if (keyResult.deadlineType === HARD_SOFT) {
                        const softDeadlineDate = DateFormatterString(keyResult.deadlineSoft, lang).date
                        const softDeadline = lang.keyResultSoftDeadlineDescription.replace("{0}", softDeadlineDate)
                        deadline = `${deadline}, ${softDeadline}`
                    }
                }

                return (
                    <TitleWrapper>
                        <Link
                            href={href}
                            onClick={() => dispatch(addModal({ url: href }))}
                        >
                            {keyResult.title}
                        </Link>
                        <TargetDescription>{targetDescription}</TargetDescription>
                        { deadline && <DeadlineDescription>{deadline}</DeadlineDescription> }
                    </TitleWrapper>
                )
            },
        },
        {
            field: "owners",
            header: lang.owners,
            cellMinWidth: "150px",
            formatter: (keyResult: OkrKeyResult) => {
                if (!keyResult.owners) return null

                const dataList: TablePopoverData[] = []
                keyResult.owners.forEach((row) => {
                    const user = userList[row.userId]
                    dataList.push({
                        id: `${user.id}_${keyResult.id}`,
                        title: <UserIcon user={user} size="sm" />,
                    })
                })

                return (
                    <TablePopover
                        id={`keyResultUsers${keyResult.id}`}
                        dataList={dataList}
                        placement="left"
                        listStyle={false}
                        title={lang.owners}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "confidence",
            header: lang.confidence,
            cellMinWidth: "80px",
            formatter: (keyResult: OkrKeyResult) => {
                const krUpdates = updateList[keyResult.id]
                const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
                if (lastUpdate) {
                    return (
                        <Confidence confidenceLevel={lastUpdate.confidenceLevel}>
                            {lang[lastUpdate.confidenceLevel]}
                        </Confidence>
                    )
                }
                return null
            },
        },
        {
            field: "progress",
            header: lang.progress,
            cellMinWidth: "80px",
            formatter: (keyResult: OkrKeyResult) => {
                const krUpdates = updateList[keyResult.id]
                const lastUpdate = krUpdates && krUpdates.length > 0 ? krUpdates[krUpdates.length - 1] : null
                if (lastUpdate) return <ProgressPercents percents={lastUpdate.progress}>{`${lastUpdate.progress}%`}</ProgressPercents>
                return <ProgressPercents percents={0}>0%</ProgressPercents>
            },
        },
    ]

    if ((editAccess || isOwner) && isOpenSession) {
        columns.unshift({
            field: "sort",
            centerAlign: true,
            onClick: (keyResult: OkrKeyResult, e) => showContextMenu(e, keyResult),
        })
    } else {
        columns.unshift({
            field: "menu",
            formatter: (keyResult: OkrKeyResult) => <TableMenu onClick={(e) => showContextMenu(e, keyResult)} />,
        })
    }

    return columns
}
export default Columns
